import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Typography,
  Chip,
  Stack,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { 
  Rule, 
  RuleCondition, 
  RuleConditionGroup, 
  RuleAction,
  RULE_CATEGORIES,
  RULE_TRIGGERS,
  CONDITION_OPERATORS,
  ACTION_TYPES,
  DATA_TYPES,
  CreateRuleRequest,
  UpdateRuleRequest,
  RuleTestResult
} from '../../types/rules';

interface RuleBuilderProps {
  rule?: Rule;
  onSave: (rule: CreateRuleRequest | UpdateRuleRequest) => Promise<void>;
  onTest?: (input: Record<string, unknown>) => Promise<RuleTestResult>;
  onCancel: () => void;
  isLoading?: boolean;
}

const RuleBuilder: React.FC<RuleBuilderProps> = ({
  rule,
  onSave,
  onTest,
  onCancel,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<Partial<Rule>>({
    name: rule?.name || '',
    description: rule?.description || '',
    category: rule?.category || 'Credit Scoring',
    priority: rule?.priority || 1,
    isActive: rule?.isActive ?? true,
    conditions: rule?.conditions || { operator: 'AND', rules: [], groups: [] },
    actions: rule?.actions || [],
    triggers: rule?.triggers || [],
    tags: rule?.tags || []
  });

  const [testInput, /* setTestInput */] = useState('{}');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const handleInputChange = (field: keyof Rule, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setValidationErrors([]);
  };

  const addCondition = (groupPath: number[] = []) => {
    const newCondition: RuleCondition = {
      field: '',
      operator: 'equals',
      value: '',
      dataType: 'string'
    };

    const updatedConditions = { ...formData.conditions };
    let targetGroup = updatedConditions;

    // Navigate to the target group
    for (const index of groupPath) {
      if (targetGroup.groups && targetGroup.groups[index]) {
        targetGroup = targetGroup.groups[index];
      }
    }

    if (!targetGroup.rules) {
      targetGroup.rules = [];
    }
    targetGroup.rules.push(newCondition);

    handleInputChange('conditions', updatedConditions);
  };

  const removeCondition = (conditionIndex: number, groupPath: number[] = []) => {
    const updatedConditions = { ...formData.conditions };
    let targetGroup = updatedConditions;

    // Navigate to the target group
    for (const index of groupPath) {
      if (targetGroup.groups && targetGroup.groups[index]) {
        targetGroup = targetGroup.groups[index];
      }
    }

    if (targetGroup.rules) {
      targetGroup.rules.splice(conditionIndex, 1);
    }

    handleInputChange('conditions', updatedConditions);
  };

  const updateCondition = (conditionIndex: number, updates: Partial<RuleCondition>, groupPath: number[] = []) => {
    const updatedConditions = { ...formData.conditions };
    let targetGroup = updatedConditions;

    // Navigate to the target group
    for (const index of groupPath) {
      if (targetGroup.groups && targetGroup.groups[index]) {
        targetGroup = targetGroup.groups[index];
      }
    }

    if (targetGroup.rules && targetGroup.rules[conditionIndex]) {
      targetGroup.rules[conditionIndex] = { ...targetGroup.rules[conditionIndex], ...updates };
    }

    handleInputChange('conditions', updatedConditions);
  };

  const addConditionGroup = (groupPath: number[] = []) => {
    const newGroup: RuleConditionGroup = {
      operator: 'AND',
      rules: [],
      groups: []
    };

    const updatedConditions = { ...formData.conditions };
    let targetGroup = updatedConditions;

    // Navigate to the target group
    for (const index of groupPath) {
      if (targetGroup.groups && targetGroup.groups[index]) {
        targetGroup = targetGroup.groups[index];
      }
    }

    if (!targetGroup.groups) {
      targetGroup.groups = [];
    }
    targetGroup.groups.push(newGroup);

    handleInputChange('conditions', updatedConditions);
  };

  const addAction = () => {
    const newAction: RuleAction = {
      type: 'set_field',
      parameters: {}
    };

    handleInputChange('actions', [...(formData.actions || []), newAction]);
  };

  const removeAction = (index: number) => {
    const updatedActions = [...(formData.actions || [])];
    updatedActions.splice(index, 1);
    handleInputChange('actions', updatedActions);
  };

  const updateAction = (index: number, updates: Partial<RuleAction>) => {
    const updatedActions = [...(formData.actions || [])];
    updatedActions[index] = { ...updatedActions[index], ...updates };
    handleInputChange('actions', updatedActions);
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];

    if (!formData.name?.trim()) {
      errors.push('Rule name is required');
    }

    if (!formData.category) {
      errors.push('Category is required');
    }

    if (!formData.triggers?.length) {
      errors.push('At least one trigger is required');
    }

    if (!formData.conditions?.rules?.length && !formData.conditions?.groups?.length) {
      errors.push('At least one condition is required');
    }

    if (!formData.actions?.length) {
      errors.push('At least one action is required');
    }

    // Validate conditions
    const validateConditionGroup = (group: RuleConditionGroup, path = ''): void => {
      group.rules?.forEach((condition, index) => {
        if (!condition.field) {
          errors.push(`Condition ${path}${index + 1}: Field is required`);
        }
        if (!condition.operator) {
          errors.push(`Condition ${path}${index + 1}: Operator is required`);
        }
      });

      group.groups?.forEach((subGroup, index) => {
        validateConditionGroup(subGroup, `${path}Group ${index + 1} - `);
      });
    };

    if (formData.conditions) {
      validateConditionGroup(formData.conditions);
    }

    // Validate actions
    formData.actions?.forEach((action, index) => {
      if (!action.type) {
        errors.push(`Action ${index + 1}: Type is required`);
      }
    });

    return errors;
  };

  const handleSave = async () => {
    const errors = validateForm();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      await onSave(formData as CreateRuleRequest);
    } catch (error) {
      console.error('Error saving rule:', error);
    }
  };

  const handleTest = async () => {
    if (!onTest) return;

    try {
      const input = JSON.parse(testInput);
      await onTest(input);
    } catch (error) {
      console.error('Error testing rule:', error);
    }
  };

  const renderConditionBuilder = (group: RuleConditionGroup, groupPath: number[] = [], depth = 0): React.ReactNode => {
    return (
      <Card variant="outlined" sx={{ mt: 0.5, ml: depth * 1.5 }}>
        <CardContent sx={{ pb: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 80, mr: 1.5 }}>
              <InputLabel>Logic</InputLabel>
              <Select
                label="Logic"
                value={group.operator}
                onChange={(e) => {
                  const updatedConditions = { ...formData.conditions };
                  let targetGroup = updatedConditions;
                  
                  for (const index of groupPath) {
                    if (targetGroup.groups && targetGroup.groups[index]) {
                      targetGroup = targetGroup.groups[index];
                    }
                  }
                  
                  targetGroup.operator = e.target.value as 'AND' | 'OR';
                  handleInputChange('conditions', updatedConditions);
                }}
              >
                <MenuItem value="AND">AND</MenuItem>
                <MenuItem value="OR">OR</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => addCondition(groupPath)}
              sx={{ mr: 0.5 }}
            >
              Add Condition
            </Button>
            
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => addConditionGroup(groupPath)}
            >
              Add Group
            </Button>
          </Box>

          {group.rules?.map((condition, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.75, gap: 0.75 }}>
              <TextField
                size="small"
                label="Field"
                value={condition.field}
                onChange={(e) => updateCondition(index, { field: e.target.value }, groupPath)}
                sx={{ flex: 1 }}
              />
              
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Operator</InputLabel>
                <Select
                  label="Operator"
                  value={condition.operator}
                  onChange={(e) => updateCondition(index, { operator: e.target.value }, groupPath)}
                >
                  {CONDITION_OPERATORS.map(op => (
                    <MenuItem key={op} value={op}>{op.replace(/_/g, ' ')}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                size="small"
                label="Value"
                value={condition.value}
                onChange={(e) => updateCondition(index, { value: e.target.value }, groupPath)}
                sx={{ flex: 1 }}
              />
              
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={condition.dataType}
                  onChange={(e) => updateCondition(index, { dataType: e.target.value as RuleCondition['dataType'] }, groupPath)}
                >
                  {DATA_TYPES.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <IconButton
                size="small"
                onClick={() => removeCondition(index, groupPath)}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}

          {group.groups?.map((subGroup, index) => (
            <div key={index}>
              {renderConditionBuilder(subGroup, [...groupPath, index], depth + 1)}
            </div>
          ))}
        </CardContent>
      </Card>
    );
  };

  const renderActionBuilder = (): React.ReactNode => {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h6">Actions</Typography>
          <Button startIcon={<AddIcon />} onClick={addAction}>
            Add Action
          </Button>
        </Box>

        {formData.actions?.map((action, index) => (
          <Card key={index} variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent sx={{ pb: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 150, mr: 1.5 }}>
                  <InputLabel>Action Type</InputLabel>
                  <Select
                    label="Action Type"
                    value={action.type}
                    onChange={(e) => updateAction(index, { type: e.target.value })}
                  >
                    {ACTION_TYPES.map(type => (
                      <MenuItem key={type} value={type}>
                        {type.replace(/_/g, ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <IconButton
                  size="small"
                  onClick={() => removeAction(index)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Parameters (JSON)"
                value={JSON.stringify(action.parameters, null, 2)}
                onChange={(e) => {
                  try {
                    const parameters = JSON.parse(e.target.value);
                    updateAction(index, { parameters });
                  } catch {
                    // Invalid JSON, but don't block typing - store as string for now
                    updateAction(index, { parameters: { _raw: e.target.value } });
                  }
                }}
              />
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2.5 }}>
      <Typography variant="h4" sx={{ mb: 2.5 }}>
        {rule ? 'Edit Rule' : 'Create Rule'}
      </Typography>

      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          <Typography variant="subtitle2">Please fix the following errors:</Typography>
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Basic Information</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2.5}>
            <TextField
              fullWidth
              label="Rule Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
            />

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                >
                  {RULE_CATEGORIES.map(category => (
                    <MenuItem key={category} value={category}>{category}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                type="number"
                label="Priority"
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', parseInt(e.target.value))}
                sx={{ width: 120 }}
                inputProps={{ min: 1, max: 100 }}
              />
            </Box>

            <FormControl>
              <InputLabel>Triggers</InputLabel>
              <Select
                label="Triggers"
                multiple
                value={formData.triggers || []}
                onChange={(e) => handleInputChange('triggers', e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value.replace(/_/g, ' ')} size="small" />
                    ))}
                  </Box>
                )}
              >
                {RULE_TRIGGERS.map(trigger => (
                  <MenuItem key={trigger} value={trigger}>
                    {trigger.replace(/_/g, ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                />
              }
              label="Active"
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Conditions</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {formData.conditions && renderConditionBuilder(formData.conditions)}
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Actions</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {renderActionBuilder()}
        </AccordionDetails>
      </Accordion>

      {onTest && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Test Rule</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Test Input (JSON)"
              value={testInput}
              // onChange={(e) => setTestInput(e.target.value)}
              placeholder='{"creditScore": 700, "income": 75000}'
              sx={{ mb: 1.5 }}
            />
            <Button
              startIcon={<PlayIcon />}
              onClick={handleTest}
              variant="outlined"
            >
              Test Rule
            </Button>
          </AccordionDetails>
        </Accordion>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2.5 }}>
        <Button onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save Rule'}
        </Button>
      </Box>
    </Box>
  );
};

export default RuleBuilder;